import { Modal, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { RefundableEnum } from '@common-types';
import { RootState, formatDateCheckInOut, isSpanish, renderNumber } from '@share/utils';
import { es } from 'date-fns/esm/locale';
import { getAmenityImageByName } from '@utils';
import { Currency, HotelStars } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import NoImage from '@assets/images/amenities/no-picture.png';

import { useQuoteModalCompare } from './useQuoteModalCompare';
import {
  getModalCompareMealPlanBadgeKind,
  MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS,
} from '../result/result-wrapper/modal-compare/meal-plan-badge-theme';

import './style.scss';

const modalWidth = 900;
const percents = 100;

interface IProps {
  visible: boolean;
  quoteBase: string;
  onCancel: () => void;
}

export function QuoteModalCompare(props: IProps) {
  const { visible, quoteBase, onCancel } = props;
  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const {
    myQuotes,
    isHistorical,
    fontFamily,
    secondaryFontFamily,
    handleView,
    modalOpen,
  } = useQuoteModalCompare({ quoteStore, loginStore, quoteBase });

  const compareRowHasMealPlan = myQuotes.some((q) => !isEmpty(q.mealPLan?.trim()));

  return (
    <Modal
      className={`modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible && modalOpen}
      footer={null}
      onCancel={onCancel}
      wrapClassName="modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="modal-compare__title-row">
          <h4 className="modal-compare__title secondary-font">
            <FormattedMessage id="results.hotel.card.compare.modal.title" />
          </h4>
        </div>

        <div className="modal-compare__hotels modal-compare__hotels--meta">
          <div className="hotels-dates">
            {myQuotes[0]?.checkIn
              ? formatDateCheckInOut(myQuotes[0]?.checkIn, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)
              : 'NA'}{' '}
            -{' '}
            {myQuotes[0]?.checkOut
              ? formatDateCheckInOut(myQuotes[0]?.checkOut, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)
              : 'NA'}
          </div>
          <div className="hotels-dates">{myQuotes[0]?.occupancy}</div>
        </div>

        <div className="modal-compare__hotels">
          {myQuotes.map((quote, index) => {
            const savings =
              quote.publicPrice && quote.price && quote.publicPrice > 0
                ? Math.floor(percents - (quote.price * percents) / quote.publicPrice)
                : 0;

            return (
              <div key={index} className="modal-compare__hotel">
                <div className="modal-compare__media">
                  <div className="hotel-images-container">
                    <div className="hotel-image" style={{ backgroundImage: `url(${quote.image})` }} />
                    {savings > 0 ? (
                      <div className="modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div
                  className={`hotel-text-container hotel-text-container--title${
                    compareRowHasMealPlan ? ' hotel-text-container--title-with-mealplan' : ''
                  }`}
                >
                  {compareRowHasMealPlan ? (
                    <div className="modal-compare__title-mealplan-row">
                      <div className="modal-compare__title-col">
                        <Tooltip title={quote.displayName} placement="top">
                          <p>{quote.displayName}</p>
                        </Tooltip>
                      </div>
                      <div className="modal-compare__meal-plan-col">
                        {!isEmpty(quote.mealPLan?.trim()) ? (
                          <span
                            className="modal-compare__meal-plan-badge"
                            style={
                              MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS[
                                getModalCompareMealPlanBadgeKind(quote.mealPLan)
                              ]
                            }
                          >
                            {quote.mealPLan}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <Tooltip title={quote.displayName} placement="top">
                      <p>{quote.displayName}</p>
                    </Tooltip>
                  )}
                </div>

                <div className="hotel-text-container hotel-text-container--address">
                  <Tooltip title={quote.fullAddress} placement="top">
                    <p>{quote.fullAddress}</p>
                  </Tooltip>
                </div>

                {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                  <div className="hotel-text-container hotel-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {quote.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {quote.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {quote.starRating >= 0 || quote.rating || quote.reviewsCount ? (
                  <div className="hotel-amenities-container">
                    {quote.starRating >= 0 ? <HotelStars stars={quote.starRating} /> : null}
                    <label>
                      {quote.rating ? (
                        <>
                          <strong>{quote.rating}</strong>/10
                        </>
                      ) : null}{' '}
                      {quote.reviewsCount ? (
                        <>
                          ({quote.reviewsCount} <FormattedMessage id="reviews" />)
                        </>
                      ) : null}
                    </label>
                  </div>
                ) : null}

                <div className="hotel-text-container hotel-text-container--price">
                  {quote.publicPrice ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.price, 2)}
                    </strong>
                  </p>
                </div>

                <div
                  className={`hotel-text-container${isHistorical ? ' hotel-text-container--flush-bottom' : ''}`}
                  style={{ height: '56px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                >
                  {quote?.amenities?.length ? (
                    <>
                      {quote?.amenities?.filter((a) => !isEmpty(a))?.length > 0 ? (
                        quote
                          ?.amenities.map((a: string) => ({
                            ...((getAmenityImageByName(a) ?? {}) as unknown as Record<string, unknown>),
                            name: a,
                          }))
                          .filter((a) => !!a)
                          .slice(0, 4)
                          .map((a: any, i) => (
                            <div key={i} className="modal-compare__amenity-row">
                              <Tooltip title={a.name || ''} placement="top">
                                <img 
                                  src={(a as { image?: string }).image || NoImage} alt={a.name || 'no-picture'}
                                  width="33"
                                  style={a?.isSVG ? { padding: '5px' } : {}}
                                />
                              </Tooltip>
                            </div>
                          ))
                      ) : (
                        <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                      )}
                    </>
                  ) : (
                    <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                  )}
                </div>

                {!isHistorical ? (
                  <div className="hotel-view-container">
                    <div className="view-button" onClick={() => handleView(quote)}>
                      <FormattedMessage id="results.hotel.card.compare.modal.view" />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
